import { ContextStore } from 'bernie-plugin-mobx';
import { formatDateString } from 'bernie-l10n';
import { parseISODateString } from '.';
import { DateInput, HotelsDate, FlightsDate } from '__generated__/api/';
import HotelsDateField = HotelsDate.Fragment;
import FlightsDateField = FlightsDate.Fragment;

//Based on https://confluence.expedia.biz/display/GRO/Localisation+Formats+for+i18n+Platform+Library
enum DateFormat {
  DAY_MONTH_YEAR = 'dayMonthYear',
  MONTH_DAY_YEAR = 'monthDayYear',
  YEAR_MONTH_DAY = 'yearMonthDay'
}

enum Locale {
  EN_AU = 'en_AU',
  EN_CA = 'en_CA',
  EN_GB = 'en_GB',
  EN_IE = 'en_IE',
  EN_MY = 'en_MY',
  EN_SG = 'en_SG',
  ES_AR = 'es_AR',
  ES_ES = 'es_ES',
  DA_DK = 'da_DK',
  DE_AT = 'de_AT',
  DE_DE = 'de_DE',
  DE_CH = 'de_CH',
  FI_FI = 'fi_FI',
  FR_CH = 'fr_CH',
  FR_BE = 'fr_BE',
  FR_CA = 'fr_CA',
  FR_FR = 'fr_FR',
  NL_BE = 'nl_BE',
  NL_NL = 'nl_NL',
  NB_NO = 'nb_NO',
  PT_BR = 'pt_BR',
  ZH_HK = 'zh_HK',
  VI_VN = 'vi_VN',
  IT_IT = 'it_IT',
  MS_MY = 'ms_MY',
  ID_ID = 'id_ID',
  TH_TH = 'th_TH',
  EN_PH = 'en_PH',
  EN_HK = 'en_HK',
  JA_JP = 'ja_JP',
  KO_KR = 'ko_KR',
  SV_SE = 'sv_SE',
  ZH_CN = 'zh_CN',
  ZH_TW = 'zh_TW',
  CA_ES = 'ca_ES'
}

interface DateFormatType {
  locale: string;
  format: string;
}

type DateType = { day: number; month: number; year: number };

interface FormattedDateType {
  dayMonthYear: DateType;
  monthDayYear: DateType;
  yearMonthDay: DateType;
}

const DATE_FORMATS_BY_LOCALE: DateFormatType[] = [
  { locale: Locale.EN_AU, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.EN_CA, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.EN_GB, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.EN_IE, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.EN_MY, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.EN_SG, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.ES_AR, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.ES_ES, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.DA_DK, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.DE_AT, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.DE_DE, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.DE_CH, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.FI_FI, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.FR_CH, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.FR_BE, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.FR_CA, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.FR_FR, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.NL_BE, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.NL_NL, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.NB_NO, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.PT_BR, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.ZH_HK, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.VI_VN, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.IT_IT, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.MS_MY, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.ID_ID, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.TH_TH, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.CA_ES, format: DateFormat.DAY_MONTH_YEAR },
  { locale: Locale.EN_PH, format: DateFormat.MONTH_DAY_YEAR },
  { locale: Locale.EN_HK, format: DateFormat.YEAR_MONTH_DAY },
  { locale: Locale.JA_JP, format: DateFormat.YEAR_MONTH_DAY },
  { locale: Locale.KO_KR, format: DateFormat.YEAR_MONTH_DAY },
  { locale: Locale.SV_SE, format: DateFormat.YEAR_MONTH_DAY },
  { locale: Locale.ZH_CN, format: DateFormat.YEAR_MONTH_DAY },
  { locale: Locale.ZH_TW, format: DateFormat.YEAR_MONTH_DAY }
];

export const formatDateToIsoString = ({ year, month, day }: DateInput): string => {
  const withLeadingZero = (dateSection: number) => dateSection.toString().padStart(2, '0');

  return `${year}-${withLeadingZero(month)}-${withLeadingZero(day)}`;
};

const formatDateStringForEnUsZhCn = (localDateFormat: string) => {
  const value = localDateFormat.split('')[0].toLocaleLowerCase();
  if (value === 'm') {
    return 'monthDayYear';
  } else if (value === 'd') {
    return 'dayMonthYear';
  }

  return 'yearMonthDay';
};

const dateValidator = (dateFormats: FormattedDateType, dateType: string): string => {
  let formattedDate = dateFormats[dateType];
  [dateType, 'dayMonthYear', 'monthDayYear', 'yearMonthDay'].some((currentDateType: string) => {
    const isValidDateFormat = dateFormats[currentDateType].day <= 31 && dateFormats[currentDateType].month <= 12;
    if (isValidDateFormat) {
      formattedDate = dateFormats[currentDateType];
    }

    return isValidDateFormat;
  });

  return formattedDate;
};

export const formatDate = (rawDate: string, locale: string, localDateFormat: string) => {
  const departure = rawDate
    .replace('TANYT', '')
    .split(/[\/.-]/)
    .map(i => parseInt(i, 10));

  const dateFormats = {
    dayMonthYear: {
      day: departure[0],
      month: departure[1],
      year: departure[2]
    },
    monthDayYear: {
      month: departure[0],
      day: departure[1],
      year: departure[2]
    },
    yearMonthDay: {
      year: departure[0],
      month: departure[1],
      day: departure[2]
    }
  };

  let formattedDate;

  switch (locale) {
    case 'en_US':
    case 'zh_CN':
      formattedDate = dateFormats[formatDateStringForEnUsZhCn(localDateFormat)];
      break;
    case 'es_MX':
      formattedDate =
        dateFormats[localDateFormat.split('')[0].toLocaleLowerCase() === 'd' ? 'dayMonthYear' : 'monthDayYear'];
      break;
    case 'sv_SE':
    case 'en_GB':
    case 'zh_HK':
      formattedDate = dateValidator(
        dateFormats,
        DATE_FORMATS_BY_LOCALE.find(dateFormat => dateFormat.locale === locale).format
      );
      break;
    default:
      formattedDate = dateFormats[DATE_FORMATS_BY_LOCALE.find(dateFormat => dateFormat.locale === locale).format];
      break;
  }

  return formattedDate;
};

export const formatDateInputIntoString = (dateInput: DateInput, context: ContextStore) => {
  const isoDateString = formatDateToIsoString(dateInput);
  const formattedDate = formatDateString(isoDateString, {
    raw: context.localDateFormat
  });

  return formattedDate;
};

export const setDateField = (selectedDateField: HotelsDateField | FlightsDateField, newDate: Date): void => {
  const emptyDateField: FlightsDate.Date = {
    __typename: 'Date',
    isoFormat: null,
    day: null,
    month: null,
    year: null,
    formatted: null
  };

  if (!newDate) {
    selectedDateField.date = { ...emptyDateField };

    return;
  }

  selectedDateField.date = selectedDateField.date || { ...emptyDateField };

  selectedDateField.date.isoFormat = parseISODateString(newDate);
  selectedDateField.date.day = newDate.getDate();
  selectedDateField.date.month = newDate.getMonth() + 1;
  selectedDateField.date.year = newDate.getFullYear();
};
